import { Avatar, Stack, Typography } from '@mui/material';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { B2CLoginError, GreenInvoiceIcon } from 'Images';
import { format } from 'date-fns';
import { shorterDateFormat } from 'Config';
import { IInvoice } from 'Context/CannabisContext';
import { DuplicateFileButtons } from './DuplicateFileButtons';

interface IDuplicateFileDialog {
  deleteInvoice: () => void;
  invoice: IInvoice | undefined;
  newInvoice: () => void;
}

export const DuplicateFileDialog = ({
  deleteInvoice,
  invoice,
  newInvoice,
}: IDuplicateFileDialog) => {
  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.Reimbursements
  ).DuplicateFileDialog;
  const isDesktop = useIsDesktop();

  return (
    <Stack
      alignItems={'center'}
      p={isDesktop ? '80px 85px 60px' : '88px 24px 48px'}
      gap={'24px'}
    >
      <Avatar
        alt=''
        src={B2CLoginError}
        sx={
          isDesktop
            ? { width: '159.25px', height: '142px' }
            : { width: '120px', height: '107px' }
        }
      />
      <Stack alignItems='center' gap='16px'>
        <Typography
          component={'h1'}
          sx={
            isDesktop
              ? { fontSize: '24px', fontWeight: 600, lineHeight: '31px' }
              : { fontSize: '20px', fontWeight: 600, lineHeight: '24px' }
          }
        >
          {dictionary.title}
        </Typography>
        <Typography
          variant='body3'
          fontWeight={isDesktop ? '600' : '400'}
          fontSize={isDesktop ? '24px' : '18px'}
          lineHeight={isDesktop ? '31px' : '24px'}
          textAlign={isDesktop ? undefined : 'center'}
        >
          {dictionary.subtitle}
        </Typography>
      </Stack>
      <Stack
        sx={{
          p: '20px 16px',
          bgcolor: 'white',
          borderRadius: '12px',
          gap: '16px',
          mb: '30px',
        }}
      >
        <Stack direction={'row'} gap='20px' alignItems='center'>
          <Avatar
            alt=''
            src={GreenInvoiceIcon}
            sx={{ width: '56px', height: '56px' }}
          />
          <Stack
            direction={isDesktop ? 'row' : 'column'}
            gap={isDesktop ? '48px' : '4px'}
          >
            <Stack
              alignItems='flex-start'
              gap={isDesktop ? undefined : '4px'}
              direction={isDesktop ? 'column' : 'row'}
              width='fit-content'
            >
              <Typography variant={isDesktop ? 'body6' : 'body3'}>{`${
                dictionary.number
              }${isDesktop ? '' : ':'}`}</Typography>
              <Typography variant={isDesktop ? 'body2' : 'body3'}>
                {invoice?.invoiceId}
              </Typography>
            </Stack>
            <Stack
              alignItems='flex-start'
              gap={isDesktop ? undefined : '4px'}
              direction={isDesktop ? 'column' : 'row'}
              width='fit-content'
            >
              <Typography variant={isDesktop ? 'body6' : 'body3'}>{`${
                dictionary.date
              }${isDesktop ? '' : ':'}`}</Typography>
              <Typography variant={isDesktop ? 'body2' : 'body3'}>
                {invoice?.invoiceDate &&
                  format(invoice.invoiceDate, shorterDateFormat)}
              </Typography>
            </Stack>
            <Stack
              alignItems='flex-start'
              gap={isDesktop ? undefined : '4px'}
              direction={isDesktop ? 'column' : 'row'}
              width='fit-content'
            >
              <Typography variant={isDesktop ? 'body6' : 'body1'}>{`${
                dictionary.amount
              }${isDesktop ? '' : ':'}`}</Typography>
              <Typography variant={isDesktop ? 'body2' : 'body1'}>
                {`₪${invoice?.invoiceTotal?.toLocaleString()}`}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <DuplicateFileButtons
        deleteLabel={dictionary.delete}
        newInvoiceLabel={dictionary.newInvoice}
        deleteInvoice={deleteInvoice}
        newInvoice={newInvoice}
      />
    </Stack>
  );
};
